<template>
  <div class="handwrite-search">
    <div class="title">手写搜字</div>
    <div class="handwrite-area">
      <div class="handwrite-preview">请在此处书写汉字</div>
      <div class="canvas-container">
        <canvas id="canvas" ref="canvas"></canvas>
      </div>
    </div>
    <div class="tool-bar">
      <div
        class="tool-bar-item"
        :class="{ active: canUndo }"
        @click="undoStroke"
      >
        <img
          :src="
            canUndo
              ? require('../../../assets/tool-bar-prev.png')
              : require('../../../assets/tool-bar-prev-disabled.png')
          "
          alt="tool-bar-item"
        />
        <div class="tool-bar-item-text">上一步</div>
      </div>
      <div
        class="tool-bar-item"
        :class="{ active: canRedo }"
        @click="redoStroke"
      >
        <img
          :src="
            canRedo
              ? require('../../../assets/tool-bar-next.png')
              : require('../../../assets/tool-bar-next-disabled.png')
          "
          alt="tool-bar-item"
        />
        <div class="tool-bar-item-text">下一步</div>
      </div>
    </div>

    <div class="clean-bar">
      <div
        class="clean-bar-item"
        @click="clearCanvas"
      >
        <img
          :src="require('../../../assets/clean-bar.png')"
          alt="tool-bar-item"
        />
        <div class="clean-bar-item-text">清空</div>
      </div>
    </div>
    <!-- 猜你想搜 -->
    <div v-if="ocrResults.length > 0" class="guess-you-search">
      <div class="guess-you-search-title">猜你想搜</div>
      <div class="guess-you-search-list">
        <div
          v-for="(item, index) in ocrResults"
          :key="index"
          class="guess-you-search-item"
          :class="{ active: index === activeIndex }"
          @click="selectChar(item, index)"
        >
          <div class="guess-you-search-item-text">
            {{ item }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HandwriteSearch",
  data() {
    return {
      canvas: null,
      ctx: null,
      drawing: false,
      lastX: 0,
      lastY: 0,
      strokes: [], // 存储所有笔划
      currentStroke: [], // 当前正在绘制的笔划
      redoStack: [], // 撤销的笔划存储在这里，用于重做
      lineWidth: 6,
      lineColor: "#000000",
      canUndo: false,
      canRedo: false,
      recognitionText: "", // OCR识别的文字
      ocrResults: [], // OCR识别结果列表
      activeIndex: -1, // 当前选中的字符索引
      recognizeTimeout: null, // 防抖定时器
    };
  },
  mounted() {
    this.initCanvas();
    window.addEventListener("resize", this.resizeCanvas);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeCanvas);
    // 清除防抖定时器
    if (this.recognizeTimeout) {
      clearTimeout(this.recognizeTimeout);
    }
  },
  methods: {
    initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext("2d");

      // 设置画布尺寸
      this.resizeCanvas();

      // 初始化画笔样式
      this.ctx.lineWidth = this.lineWidth;
      this.ctx.lineJoin = "round";
      this.ctx.lineCap = "round";
      this.ctx.strokeStyle = this.lineColor;

      // 添加事件监听
      this.canvas.addEventListener("mousedown", this.startDrawing);
      this.canvas.addEventListener("mousemove", this.draw);
      this.canvas.addEventListener("mouseup", this.stopDrawing);
      this.canvas.addEventListener("mouseout", this.stopDrawing);

      // 触摸设备支持
      this.canvas.addEventListener("touchstart", this.handleTouchStart);
      this.canvas.addEventListener("touchmove", this.handleTouchMove);
      this.canvas.addEventListener("touchend", this.handleTouchEnd);
    },

    resizeCanvas() {
      const container = this.canvas.parentElement;
      this.canvas.width = container.clientWidth;
      this.canvas.height = container.clientHeight;
      this.redrawCanvas();
    },

    startDrawing(e) {
      this.drawing = true;
      const rect = this.canvas.getBoundingClientRect();
      this.lastX = e.clientX - rect.left;
      this.lastY = e.clientY - rect.top;

      // 开始新的笔划
      this.currentStroke = [
        {
          x: this.lastX,
          y: this.lastY,
          lineWidth: this.lineWidth,
          lineColor: this.lineColor,
        },
      ];

      // 清空重做栈
      if (this.redoStack.length > 0) {
        this.redoStack = [];
        this.updateCanRedo();
      }
    },

    draw(e) {
      if (!this.drawing) return;

      const rect = this.canvas.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      this.ctx.beginPath();
      this.ctx.moveTo(this.lastX, this.lastY);
      this.ctx.lineTo(currentX, currentY);
      this.ctx.stroke();

      // 记录当前点
      this.currentStroke.push({
        x: currentX,
        y: currentY,
        lineWidth: this.lineWidth,
        lineColor: this.lineColor,
      });

      this.lastX = currentX;
      this.lastY = currentY;
    },

    stopDrawing() {
      if (!this.drawing) return;
      this.drawing = false;

      // 如果当前笔划有效（至少有两个点），则添加到笔划列表
      if (this.currentStroke.length > 1) {
        this.strokes.push([...this.currentStroke]);
        this.updateCanUndo();

        // 防抖调用OCR识别
        this.debounceRecognize();
      }

      this.currentStroke = [];
    },

    // 触摸设备支持
    handleTouchStart(e) {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousedown", {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      this.startDrawing(mouseEvent);
    },

    handleTouchMove(e) {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent("mousemove", {
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      this.draw(mouseEvent);
    },

    handleTouchEnd(e) {
      e.preventDefault();
      const mouseEvent = new MouseEvent("mouseup", {});
      this.stopDrawing(mouseEvent);
    },

    // 撤销上一笔
    undoStroke() {
      if (!this.canUndo) return;

      const removedStroke = this.strokes.pop();
      this.redoStack.push(removedStroke);

      this.redrawCanvas();
      this.updateCanUndo();
      this.updateCanRedo();

      // 重新识别
      this.debounceRecognize();
    },

    // 重做上一笔
    redoStroke() {
      if (!this.canRedo) return;

      const stroke = this.redoStack.pop();
      this.strokes.push(stroke);

      this.redrawCanvas();
      this.updateCanUndo();
      this.updateCanRedo();

      // 重新识别
      this.debounceRecognize();
    },

    // 重绘画布
    redrawCanvas() {
      // 清空画布
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      // 重新绘制所有笔划
      for (const stroke of this.strokes) {
        if (stroke.length < 2) continue;

        const firstPoint = stroke[0];
        this.ctx.lineWidth = firstPoint.lineWidth;
        this.ctx.strokeStyle = firstPoint.lineColor;

        this.ctx.beginPath();
        this.ctx.moveTo(firstPoint.x, firstPoint.y);

        for (let i = 1; i < stroke.length; i++) {
          this.ctx.lineTo(stroke[i].x, stroke[i].y);
        }

        this.ctx.stroke();
      }
    },

    // 更新撤销按钮状态
    updateCanUndo() {
      this.canUndo = this.strokes.length > 0;
    },

    // 更新重做按钮状态
    updateCanRedo() {
      this.canRedo = this.redoStack.length > 0;
    },

    // 清空画布
    clearCanvas() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.strokes = [];
      this.redoStack = [];
      this.updateCanUndo();
      this.updateCanRedo();
      this.recognitionText = "";
      this.ocrResults = [];
    },

    // 防抖函数，延迟执行OCR识别
    debounceRecognize() {
      // 清除之前的定时器
      if (this.recognizeTimeout) {
        clearTimeout(this.recognizeTimeout);
      }

      // 如果没有笔划，清空结果
      if (this.strokes.length === 0) {
        this.recognitionText = "";
        this.ocrResults = [];
        return;
      }

      // 设置新的定时器，延迟500毫秒执行OCR识别
      this.recognizeTimeout = setTimeout(() => {
        this.recognizeHandwriting();
      }, 1000);
    },

    // 调用OCR识别API
    recognizeHandwriting() {
      // 如果没有笔划，不执行识别
      if (this.strokes.length === 0) return;

      // 将canvas转换为图片数据
      const imageData = this.canvas.toDataURL("image/png");

      // 创建FormData对象
      const formData = new FormData();

      // 将Base64图片转换为Blob
      const blob = this.dataURLtoBlob(imageData);
      formData.append("file", blob, "handwriting.png");

      // 调用OCR识别接口
      this.$http
        .post(
          "https://api.xiaobixiezi.com/user/public/ocrRecognition",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then((response) => {
          if (response.data && response.data.words) {
            // 处理识别结果
            const results = response.data.words || [];
            // 更新识别结果
            this.ocrResults = Array.isArray(results) ? results : [results];

            // 如果有结果，设置第一个为当前识别文字
            if (this.ocrResults.length > 0) {
              this.selectChar(this.ocrResults[0], 0);
            } else {
              this.recognitionText = "";
              this.activeIndex = -1;
              this.$emit("characterSelected", "");
            }
          } else {
            this.ocrResults = [];
            this.recognitionText = "";
            this.activeIndex = -1;
            this.$emit("characterSelected", "");
          }
        });
    },

    // 将Base64图片数据转换为Blob
    dataURLtoBlob(dataURL) {
      const arr = dataURL.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new Blob([u8arr], { type: mime });
    },

    // 选择识别结果
    selectChar(char, index) {
      this.recognitionText = char;
      this.activeIndex = index;
      // 这里可以添加选择后的操作，比如触发搜索事件
      this.$emit("characterSelected", char);
    },
  },
};
</script>

<style scoped>
@font-face {
  font-family: "FangZhengKaiTi";
  src: url("../../../assets/fonts/FangZhengKaiTi.ttf") format("truetype");
  font-weight: normal;
  font-style: normal;
}

.handwrite-search {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0.6rem;
  position: relative;
}

.title {
  font-weight: 600;
  font-size: 1.54rem;
  color: #ffffff;
}

.handwrite-area {
  height: 36.77rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 0.46rem;
  background: #ffffff;
  border-radius: 0.92rem 0.92rem 0.92rem 0.92rem;
  position: relative;
  overflow: hidden;
}

.canvas-container {
  width: 100%;
  height: 100%;
  background: url("../../../assets/canvas-bg.png") no-repeat center center;
  background-size: 100% 100%;
  position: relative;
}

#canvas {
  width: 100%;
  height: 100%;
  cursor: crosshair;
}

.handwrite-preview {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
  font-weight: 600;
  font-size: 1.85rem;
  color: #eeeeee;
  pointer-events: none;
}

.clean-bar {
  position: absolute;
  bottom: 12.5rem;
  left: -3.54rem;
  width: 3.54rem;
  height: 4.54rem;
  background: #ffffff;
  border-top: 3px solid #0f85db;
  border-bottom: 3px solid #0f85db;
  border-left: 3px solid #0f85db;
  box-sizing: border-box;
  border-radius: 0.46rem 0rem 0rem 0.46rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
}

.clean-bar-item {
  text-align: center;
  cursor: pointer;
}
.clean-bar-item img {
  width: 1.85rem;
  height: 1.85rem;
}

.clean-bar-item-text {
  font-family: PingFang SC, PingFang SC;
  font-weight: 600;
  font-size: 0.62rem;
  color: #3d465e;
}

.tool-bar {
  position: absolute;
  bottom: 3.73rem;
  left: -3.54rem;
  width: 3.54rem;
  height: 7.92rem;
  background: #ffffff;
  border-top: 3px solid #0f85db;
  border-bottom: 3px solid #0f85db;
  border-left: 3px solid #0f85db;
  box-sizing: border-box;
  border-radius: 0.46rem 0rem 0rem 0.46rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
}

.tool-bar-item {
  text-align: center;
  cursor: pointer;
}

.tool-bar-item img {
  width: 1.85rem;
  height: 1.85rem;
}

.tool-bar-item.active {
  cursor: pointer;
}

.tool-bar-item:not(.active) {
  cursor: default;
  opacity: 0.6;
}

.tool-bar-item.active .tool-bar-item-text {
  color: #3d465e;
}

.tool-bar-item-text {
  font-family: PingFang SC, PingFang SC;
  font-weight: 600;
  font-size: 0.62rem;
  color: #def1fc;
}

::-webkit-scrollbar {
  display: none;
}

.guess-you-search {
  width: 37.85rem;
  height: 4.54rem;
  background: rgba(22, 160, 233, 0.14);
  border-radius: 0.62rem 0.62rem 0.62rem 0.62rem;
  position: absolute;
  left: 1.23rem;
  bottom: 1.23rem;
  display: flex;
  align-items: center;
}

.guess-you-search-title {
  font-weight: 600;
  font-size: 0.77rem;
  color: #16a0e9;
  writing-mode: vertical-lr;
  text-orientation: upright;
  padding-left: 0.8rem;
  letter-spacing: 2px;
}

.guess-you-search-list {
  width: 35.31rem;
  height: 3.92rem;
  background: #ffffff;
  border-radius: 0.92rem 0.92rem 0.92rem 0.92rem;
  border: 0.04rem solid #ffffff;
  margin-left: 0.2rem;
  padding: 0 0.8rem;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
}

.guess-you-search-item {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  background: url("../../../assets/search-item-bg.png") no-repeat center center;
  background-size: 100% 100%;
  border-radius: 0.46rem 0.46rem 0.46rem 0.46rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.guess-you-search-item-text {
  font-family: "FangZhengKaiTi", serif;
  font-size: 1.54rem;
  color: #b7b7b7;
}

.guess-you-search-item.active .guess-you-search-item-text {
  color: #3d465e;
}

.guess-you-search-item.active {
  transform: scale(1.1);
  background: url("../../../assets/search-item-bg-active.png") no-repeat center
    center;
  background-size: 100% 100%;
}
</style>
